import type React from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@capacit-com/lara-law-shared-ui";
import { type FC, useEffect, useState, useCallback, useMemo } from "react";
import AcceptedPrompts from "./blocks/AcceptedPrompts";
import PendingPrompts from "./blocks/PendingPrompts";
import { Input } from "@capacit-com/lara-law-shared-ui";
import { useNavigate, useLocation } from "react-router-dom";
import useAcceptedPagination from "./hooks/useAcceptedPagination";
import usePendingPagination from "./hooks/usePendingPagination";
import { DataTablePagination } from "@/components/data-table/data-table-pagination";
import PageContent from "../PageContent";
import TagsManagement from "./blocks/TagManagement";

type TabValue = "accepted" | "pending" | "tags";

const PromptsView: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState<TabValue>(
    (new URLSearchParams(location.search).get("tab") as TabValue) || "accepted"
  );

  const {
    prompts: acceptedPrompts,
    tenantUsers: acceptedTenantUsers,
    pageCount: acceptedPageCount,
    pageIndex: acceptedPageIndex,
    pageSize: acceptedPageSize,
    setPageIndex: acceptedSetPageIndex,
    setPageSize: acceptedSetPageSize,
    promptsLoading: acceptedLoading,
    handleSearchChange: acceptedHandleSearchChange,
  } = useAcceptedPagination();

  const {
    prompts: pendingPrompts,
    tenantUsers: pendingTenantUsers,
    pageCount: pendingPageCount,
    pageIndex: pendingPageIndex,
    pageSize: pendingPageSize,
    setPageIndex: pendingSetPageIndex,
    setPageSize: pendingSetPageSize,
    promptsLoading: pendingLoading,
    handleSearchChange: pendingHandleSearchChange,
  } = usePendingPagination();

  const handleTabChange = useCallback(
    (value: string) => {
      const newTab = value as TabValue;
      setActiveTab(newTab);
      setSearch("");

      const searchParams = new URLSearchParams(location.search);
      searchParams.set("tab", newTab);
      navigate(`${location.pathname}?${searchParams.toString()}`);

      if (newTab === "accepted") {
        pendingSetPageIndex(0);
      } else if (newTab === "pending") {
        acceptedSetPageIndex(0);
      }
    },
    [pendingSetPageIndex, acceptedSetPageIndex, navigate, location]
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get("tab") as TabValue;
    if (tab) {
      setActiveTab(tab);
    }
  }, [location.search]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearch(value);

      if (activeTab === "accepted") {
        acceptedHandleSearchChange(value);
      } else if (activeTab === "pending") {
        pendingHandleSearchChange(value);
      }
    },
    [activeTab, acceptedHandleSearchChange, pendingHandleSearchChange]
  );

  const shouldShowPagination =
    activeTab === "accepted" || activeTab === "pending";

  // Memoize the pagination component to prevent unnecessary rerenders
  const paginationComponent = useMemo(
    () =>
      shouldShowPagination ? (
        <div className="w-full border-t items-center justify-center py-2">
          <DataTablePagination
            pageCount={
              activeTab === "accepted" ? acceptedPageCount : pendingPageCount
            }
            pageIndex={
              activeTab === "accepted" ? acceptedPageIndex : pendingPageIndex
            }
            pageSize={
              activeTab === "accepted" ? acceptedPageSize : pendingPageSize
            }
            setPageIndex={
              activeTab === "accepted"
                ? acceptedSetPageIndex
                : pendingSetPageIndex
            }
            setPageSize={
              activeTab === "accepted"
                ? acceptedSetPageSize
                : pendingSetPageSize
            }
            pageSizeOptions={[10, 20, 30, 40, 50]}
          />
        </div>
      ) : null,
    [
      shouldShowPagination,
      activeTab,
      acceptedPageCount,
      pendingPageCount,
      acceptedPageIndex,
      pendingPageIndex,
      acceptedPageSize,
      pendingPageSize,
      acceptedSetPageIndex,
      pendingSetPageIndex,
      acceptedSetPageSize,
      pendingSetPageSize,
    ]
  );

  return (
    <PageContent title="Promptbibliotek" appFooter={paginationComponent}>
      <div className="flex flex-col">
        <Tabs
          value={activeTab}
          defaultValue="accepted"
          className="workspace-tabs flex flex-col h-full"
          onValueChange={handleTabChange}
        >
          <div className="shrink-0 px-4 py-4 sticky top-0 z-10 bg-background">
            <TabsList className="tabs-list flex justify-between w-full">
              <div>
                <TabsTrigger
                  value="accepted"
                  className="tab-trigger cursor-pointer"
                >
                  Accepterede Prompts
                </TabsTrigger>
                <TabsTrigger
                  value="pending"
                  className="tab-trigger cursor-pointer"
                >
                  Afventende Prompts
                </TabsTrigger>
                <TabsTrigger
                  value="tags"
                  className="tab-trigger cursor-pointer"
                >
                  Tags
                </TabsTrigger>
              </div>
              <Input
                type="text"
                placeholder={
                  activeTab === "tags"
                    ? "Søg efter tags..."
                    : "Søg efter prompts..."
                }
                className="w-1/4"
                value={search}
                onChange={handleSearchChange}
              />
            </TabsList>
          </div>

          <div className="h-full overflow-y-auto px-4">
            <TabsContent value="accepted" className="h-full p">
              <AcceptedPrompts
                search={search}
                loading={acceptedLoading}
                prompts={acceptedPrompts}
                tenantUsers={acceptedTenantUsers}
                handleSearchChange={acceptedHandleSearchChange}
              />
            </TabsContent>
            <TabsContent value="pending" className="h-full">
              <PendingPrompts
                search={search}
                loading={pendingLoading}
                prompts={pendingPrompts}
                tenantUsers={pendingTenantUsers}
                handleSearchChange={pendingHandleSearchChange}
              />
            </TabsContent>
            <TabsContent value="tags" className="h-full">
              <TagsManagement search={search} />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </PageContent>
  );
};

export default PromptsView;
